import { motion } from "framer-motion";
import { Sparkles, Droplets, Bath, Bug, Scissors, Wand2 } from "lucide-react";

const highlights = [
  {
    icon: Sparkles,
    title: "Premium Grooming",
    desc: "Perawatan menyeluruh dengan produk berkualitas",
  },
  {
    icon: Droplets,
    title: "Skin & Coat Care",
    desc: "Menjaga kulit dan bulu tetap sehat & berkilau",
  },
  {
    icon: Bath,
    title: "Medicated Bath",
    desc: "Mandi medis untuk kondisi kulit khusus",
  },
  {
    icon: Bug,
    title: "Flea Treatment",
    desc: "Penanganan kutu yang aman & profesional",
  },
];

const ease = [0.2, 0.8, 0.2, 1];

export default function Grooming() {
  return (
    <section
      id="grooming"
      data-testid="grooming-section"
      className="relative py-24 md:py-32 overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, var(--pikko-cream) 0%, var(--pikko-cream-deep) 100%)",
      }}
    >
      {/* decorative */}
      <div
        aria-hidden
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full opacity-30 blur-3xl"
        style={{
          background:
            "radial-gradient(circle, rgba(217,140,63,0.18), transparent 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-14 lg:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.85, ease }}
            className="lg:col-span-7"
          >
            <span className="eyebrow">— 03 / Layanan</span>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-[58px] leading-[1.05] tracking-tight text-[var(--pikko-brown-ink)]">
              Pet{" "}
              <span className="italic text-[var(--pikko-orange)]">
                Grooming
              </span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.85, delay: 0.1, ease }}
            className="lg:col-span-5 text-[15px] leading-relaxed text-[var(--pikko-brown-deep)]/85"
          >
            Perawatan kulit dan bulu dengan pendekatan profesional untuk
            membantu menjaga kebersihan serta kenyamanan hewan kesayangan Anda.
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-12 gap-7 lg:gap-9 items-stretch">
          {/* Left: Editorial card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease }}
            className="lg:col-span-5 relative rounded-[32px] p-9 lg:p-11 overflow-hidden flex flex-col justify-between min-h-[420px]"
            style={{
              background:
                "linear-gradient(150deg, var(--pikko-brown-deep) 0%, var(--pikko-brown-ink) 100%)",
            }}
          >
            <div
              aria-hidden
              className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full opacity-25 blur-3xl"
              style={{
                background:
                  "radial-gradient(circle, rgba(217,140,63,0.55), transparent 60%)",
              }}
            />
            <div className="relative">
              <div className="flex items-center gap-2.5">
                <Scissors className="w-4 h-4 text-[var(--pikko-orange)]" />
                <span className="text-[10px] uppercase tracking-[0.28em] text-[var(--pikko-cream)]/75 font-semibold">
                  Pikko Grooming Studio
                </span>
              </div>
              <h3 className="mt-7 font-serif text-[32px] sm:text-[38px] leading-[1.08] text-[var(--pikko-cream)]">
                Bath, brush <br />
                <span className="italic text-[var(--pikko-orange)]">
                  &amp; beautiful
                </span>{" "}
                care.
              </h3>
              <p className="mt-5 text-[13.5px] leading-relaxed text-[var(--pikko-cream)]/75 max-w-sm">
                Setiap sesi grooming dilakukan oleh groomer terlatih dengan
                produk yang aman, peralatan bersih, dan pendekatan yang
                menenangkan untuk hewan.
              </p>
            </div>
            <div className="relative mt-10 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[var(--pikko-orange)] flex items-center justify-center shadow-[0_18px_40px_-15px_rgba(217,140,63,0.7)]">
                <Wand2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--pikko-cream)]/60 font-semibold">
                  By appointment
                </div>
                <div className="font-serif text-[18px] text-[var(--pikko-cream)] leading-tight">
                  Tailored grooming program
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: highlight grid */}
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5 lg:gap-6">
            {highlights.map((h, i) => (
              <motion.div
                key={h.title}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.1, ease }}
                data-testid={`grooming-highlight-${i}`}
                className="soft-card p-7 lg:p-8 group hover:translate-y-[-3px] transition-transform duration-500"
              >
                <div className="w-12 h-12 rounded-2xl bg-[var(--pikko-orange)]/12 flex items-center justify-center group-hover:bg-[var(--pikko-orange)]/20 transition-colors duration-500">
                  <h.icon className="w-5 h-5 text-[var(--pikko-orange-dark)]" />
                </div>
                <h4 className="mt-5 font-serif text-[22px] leading-snug text-[var(--pikko-brown-ink)]">
                  {h.title}
                </h4>
                <p className="mt-2.5 text-[13.5px] leading-relaxed text-[var(--pikko-brown-deep)]/80">
                  {h.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
